import { useAuth } from "@/contexts/AuthContext";
import {
  useChapters,
  useUpdateChapter,
  useDeleteChapter,
} from "@/api/chapters";
import { useState, useMemo } from "react";
import {
  LogOut,
  ChevronRight,
  Landmark,
  Mail,
  KeyRound,
  Info,
  Plus,
  Loader2,
  Shield,
  Building2,
  Users,
  Trash2,
  Edit3,
  MapPin,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { CreateChapterForm } from "./components/create-chapter-form";
import { EditChapterForm } from "./components/edit-chapter-form";

/* ── Settings Row ── */
function SettingsRow({
  icon: Icon,
  label,
  value,
  destructive,
  onClick,
  badge,
}) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
        onClick && "hover:bg-muted/50 active:bg-muted",
      )}
    >
      {Icon && (
        <div
          className={cn(
            "h-8 w-8 rounded-md flex items-center justify-center shrink-0 text-sm",
            destructive
              ? "bg-destructive/10 text-destructive"
              : "bg-primary/10 text-primary",
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
      )}
      <span
        className={cn(
          "flex-1 text-sm font-medium",
          destructive ? "text-destructive" : "text-foreground",
        )}
      >
        {label}
      </span>
      {badge && (
        <Badge variant="secondary" className="text-[10px]">
          {badge}
        </Badge>
      )}
      {value && (
        <span className="text-xs text-muted-foreground mr-1 shrink-0 max-w-[40%] truncate">
          {value}
        </span>
      )}
      {onClick && !destructive && (
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      )}
    </Comp>
  );
}

/* ── Chapter Detail Sheet ── */
function ChapterDetail({ chapter, open, onClose }) {
  const [showEdit, setShowEdit] = useState(false);
  const { mutate: deleteChapter } = useDeleteChapter();

  if (!chapter) return null;

  const handleDelete = () => {
    if (
      window.confirm(
        `Delete chapter "${chapter.chapterName}"? This cannot be undone.`,
      )
    ) {
      deleteChapter(chapter._id, { onSuccess: onClose });
    }
  };

  return (
    <>
      <Dialog open={open && !showEdit} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{chapter.chapterName}</DialogTitle>
            <DialogDescription className="text-xs">
              Chapter details and management
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <Card className="border-0 bg-muted/30">
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-muted-foreground">
                    Chapter Head
                  </span>
                  <span className="text-sm font-medium">
                    {chapter.chapterHead?.name || "—"}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-muted-foreground">Contact</span>
                  <span className="text-sm">
                    {chapter.chapterHead?.contact || "—"}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-muted-foreground">Email</span>
                  <span className="text-sm">
                    {chapter.chapterHead?.email || "—"}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-muted-foreground">
                    Target Count
                  </span>
                  <span className="text-sm font-medium">
                    {chapter.targetCount || 0}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-muted-foreground">
                    Mahallus
                  </span>
                  <span className="text-sm font-medium">
                    {chapter.mahalluList?.length || 0}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-muted-foreground">Status</span>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px]",
                      chapter.status === "active"
                        ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                        : "border-amber-200 text-amber-700 bg-amber-50",
                    )}
                  >
                    {chapter.status || "active"}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {chapter.mahalluList?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2">
                  MAHALLU LIST
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {chapter.mahalluList.map((m, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {m}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1 gap-1.5"
                onClick={() => setShowEdit(true)}
              >
                <Edit3 className="h-3.5 w-3.5" />
                Edit
              </Button>
              <Button
                variant="destructive"
                className="gap-1.5"
                onClick={handleDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit {chapter.chapterName}</DialogTitle>
            <DialogDescription className="text-xs">
              Update chapter details and mahallu list
            </DialogDescription>
          </DialogHeader>
          <EditChapterForm
            chapter={chapter}
            onSuccess={() => {
              setShowEdit(false);
              onClose();
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AdminManage() {
  const { user, logout } = useAuth();
  const { data: chapters, isLoading } = useChapters();
  const [showCreateChapter, setShowCreateChapter] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);

  const sortedChapters = useMemo(
    () =>
      [...(chapters || [])].sort((a, b) =>
        a.chapterName?.localeCompare(b.chapterName),
      ),
    [chapters],
  );

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Header ── */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b -mx-3 md:-mx-6 px-3 md:px-6">
        <div className="flex items-center justify-between h-14">
          <div>
            <h1 className="text-lg font-semibold leading-tight">Manage</h1>
            <p className="text-xs text-muted-foreground">
              System Settings & Administration
            </p>
          </div>
        </div>
      </div>

      <div className="py-4 space-y-5">
        {/* ── Profile card ── */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-16 w-16 rounded-lg bg-primary/15 text-primary flex items-center justify-center text-lg font-bold shrink-0">
              {(user?.email || "A")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base font-semibold truncate">
                {user?.chapterName || "Super Admin"}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {user?.email}
              </p>
              <Badge variant="secondary" className="mt-2 text-xs gap-1">
                <Shield className="h-3 w-3" />
                Administrator
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* ── Chapters Section ── */}
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-sm font-semibold text-foreground">Chapters</h2>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => setShowCreateChapter(true)}
            >
              <Plus className="h-3 w-3" />
              Add
            </Button>
          </div>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : sortedChapters.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">
                    No chapters yet
                  </p>
                </div>
              ) : (
                sortedChapters.map((ch, i) => (
                  <div key={ch._id}>
                    <button
                      onClick={() => setSelectedChapter(ch)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/50 active:bg-muted transition-colors"
                    >
                      <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {ch.chapterName}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {ch.mahalluList?.length || 0} mahallus · Target:{" "}
                          {ch.targetCount || 0}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px]",
                          ch.status === "active"
                            ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                            : "border-amber-200 text-amber-700 bg-amber-50",
                        )}
                      >
                        {ch.status || "active"}
                      </Badge>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    </button>
                    {i < sortedChapters.length - 1 && <Separator />}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Account Section ── */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground px-1">
            Account
          </h2>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <SettingsRow icon={Mail} label="Email" value={user?.email} />
              <Separator />
              <SettingsRow
                icon={KeyRound}
                label="Change Password"
                onClick={() => {}}
              />
            </CardContent>
          </Card>
        </div>

        {/* ── More Section ── */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground px-1">More</h2>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <SettingsRow icon={Info} label="Version" value="1.0.0" />
              <Separator />
              <SettingsRow
                icon={LogOut}
                label="Sign Out"
                destructive
                onClick={logout}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Chapter Dialog */}
      <Dialog open={showCreateChapter} onOpenChange={setShowCreateChapter}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Chapter</DialogTitle>
            <DialogDescription className="text-xs">
              Add a new chapter. Login credentials will be auto-generated.
            </DialogDescription>
          </DialogHeader>
          <CreateChapterForm onSuccess={() => setShowCreateChapter(false)} />
        </DialogContent>
      </Dialog>

      {/* Chapter Detail Sheet */}
      <ChapterDetail
        chapter={selectedChapter}
        open={!!selectedChapter}
        onClose={() => setSelectedChapter(null)}
      />
    </div>
  );
}
